<template>
  <div id="app-v2">
    <AggregatorOfferHeader v-if="payload && payload.offer && payload.creditRequested" :offer="payload.offer" :creditRequested="payload.creditRequested" :balanceTransferRequestedDecision="payload.balanceTransferRequestedDecision" />
    <AggregatorOfferHeader v-else-if="payload && payload.offer" :offer="payload.offer"/>
    <IntroductionHeader v-else/>
    <!-- Check form -->
    <template>
      <div v-if="!payload || !payload.offer" class="curved-bg">
        <div class="curved"></div>
      </div>
      <div :class="{ 'no-aggregator-header': (payload && !payload.offer) || !payload }" class="mb-10 shadow-bottom-full">
        <ProgressTracker :about-you="aboutYourSelfComplete" :where-you-live="whereYouLiveComplete" :work-and-finances="workAndFinancesComplete" :keep-in-touch="keepInTouchComplete" />
      </div>
      <div class="section-container">
        <div class="font-bold section-header">
          <h2>Your Eligibility QuickCheck</h2>
        </div>
        <div class="px-2 mt-2 section-block shadow-full md:p-8 lg:rounded-lg">
          <AboutYourself/>
        </div>
        <div class="px-2 mt-10 section-block shadow-full md:p-8 lg:rounded-lg">
          <WhereYouLive/>
        </div>
        <div class="px-2 mt-10 section-block shadow-full md:p-8 lg:rounded-lg">
          <WorkAndFinances/>
        </div>
        <div class="px-12 mt-10 lg:rounded-lg">
          <KeepInTouch/>
        </div>
        <div class="p-6 rounded-lg">
          <TermsAndConditions/>
        </div>
        <div class="flex justify-center p-6 mb-8">
          <button @click="submit()" class="mx-auto-force w-full px-4 py-2 text-2xl text-white rounded-full shadow pol-button sm:w-4/6 hover:bg-polred-dark bg-polred focus:shadow-outline focus:outline-none" type="button">
            <span class="font-bold">Check now</span>
            <p class="text-base font-normal">This won't affect your credit rating</p>
          </button>
        </div>
      </div>
      <div v-if="debug" class="h-screen p-4 bg-gray-100 debug-object">
        <h1 class="mb-2 text-xl font-bold">✨ Live State</h1>
        <pre>{{ this.$store.state }}</pre>
      </div>
    </template>
    <Footer/>
  </div>
</template>

<script>

import { mapFields } from 'vuex-map-fields'
import '@/v2/assets/css/app.css'
import 'vue-loading-overlay/dist/vue-loading.css'

// headers
const IntroductionHeader = () => import('@/v2/components/headers/IntroductionHeader')
import AggregatorOfferHeader from '@/v2/components/headers/AggregatorOfferHeader'

import AboutYourself from '@/v2/components/blocks/AggregatorFlow/AboutYourself'
import WhereYouLive from '@/v2/components/blocks/AggregatorFlow/WhereYouLive'
import WorkAndFinances from '@/v2/components/blocks/AggregatorFlow/WorkAndFinances'
import Footer from '@/v2/components/Footer'
import KeepInTouch from '@/v2/components/blocks/AggregatorFlow/KeepInTouch'
import TermsAndConditions from '@/v2/components/blocks/TermsAndConditions'
import ProgressTracker from '@/v2/components/elements/AggregatorFlow/ProgressTracker'

import mixins from '@/v2/mixins'

export default {
  name: 'Home',
  mixins: [mixins],
  components: {
    AggregatorOfferHeader,
    ProgressTracker,
    AboutYourself,
    WhereYouLive,
    WorkAndFinances,
    KeepInTouch,
    TermsAndConditions,
    IntroductionHeader,
    Footer
  },
  mounted() {
    this.$root.$on('aboutYourSelfComplete', (isComplete) => { this.aboutYourSelfComplete = isComplete })
    this.$root.$on('whereYouLiveComplete', (isComplete) => { this.whereYouLiveComplete = isComplete })
    this.$root.$on('workAndFinancesComplete', (isComplete) => { this.workAndFinancesComplete = isComplete })
    this.$root.$on('keepInTouchComplete', (isComplete) => { this.keepInTouchComplete = isComplete })
  },
  async created () {

    let urlParams = new URLSearchParams(window.location.search)
    
    if (urlParams.has('source') && urlParams.get('source').length > 0) {
      if (urlParams.get('source') === 'CM01') {
        this.sourcecode = 'PCM'
      } 
      else {
        this.sourcecode = urlParams.get('source')
      }
    }

    if (urlParams.has('campaign') && urlParams.get('campaign').length > 0) {
      this.campaign = urlParams.get('campaign')
    }

    if (urlParams.has('pcr') && urlParams.get('pcr').length > 0) {
      this.pcr = urlParams.get('pcr')
    }    

    if (urlParams.has('selection') && urlParams.get('selection').length > 0) {
      this.productId = urlParams.get('selection')
    }

    if (urlParams.has('polorig') && urlParams.get('polorig').length > 0) {
      if (!urlParams.has('source') || (urlParams.has('source') && urlParams.get('source').length <= 0)) {
        this.sourcecode = "";
      }
      this.fadcode = urlParams.get('polorig')
    }

    if (urlParams.has('debug') && process.env.NODE_ENV !== 'production') {
      this.debug = urlParams.get('debug')
    }

    const aggregatorRef = process.env.VUE_APP_AGGREGATOR_ID

    if (urlParams.has(aggregatorRef)) {
          
      let aggregatorData = await this.getAggregatorData(urlParams.get(aggregatorRef))

      if (!aggregatorData) {
        return
      }

      aggregatorData = this.removeExtraRequestFields(aggregatorData)
      
      this.payload = aggregatorData
      
      if (aggregatorData.creditRequested) {
        this.creditRequested = {...this.creditRequested, ...aggregatorData.creditRequested}
        await this.$root.$emit('dob:split')

        if (aggregatorData.creditRequested.creditAmount) {
          this.creditRequested.creditAmount = aggregatorData.creditRequested.creditAmount
        }
        if (aggregatorData.creditRequested.creditType) {
          this.creditRequested.creditType = aggregatorData.creditRequested.creditType
        }

      }

      if (aggregatorData.person) {
        
        this.person = {...this.person, ...aggregatorData.person}
        await this.$root.$emit('dob:split')

        // update key ordering of current address
        if (aggregatorData.person.currentAddress) {
          this.person.currentAddress = this.sortAddressObject(aggregatorData.person.currentAddress)
        }

        // update key ordering of previous address
        if (aggregatorData.person.previousAddress) {
          this.person.previousAddress = this.sortAddressObject(aggregatorData.person.previousAddress)
        }

      }

    }
  },
  computed : {
    ...mapFields([
      'sourcecode',
      'fadcode',
      'internal.outcome',
      'internal.campaign',
      'internal.pcr',
      'internal.payload',
      'productId',
      'person',
      'balanceTransferRequestedDecision',
      'creditRequested',
      'person.birthDate',
    ]),
  },
  data() {
    return {
      debug: false,
      aboutYourSelfComplete: false,
      whereYouLiveComplete: false,
      workAndFinancesComplete: false,
      keepInTouchComplete: false,
    }
  }
}
</script>